import { useEffect, useRef, useState } from "react";
import {
  getScheduleIngestionStatus,
  startScheduleIngestion,
  startScheduleIngestionFromUrl,
  uploadScheduleFiles,
} from "../../../../services/schedule-ingestions";
import type {
  ConfirmScheduleIngestionResponse,
  ConfirmScheduleIngestionResult,
} from "../../../../types/schedule-ingestion";
import { getSafeErrorMessage } from "../../../../utils/errorHandler";
import { ModalCloseButton } from "../../../../components/ModalCloseButton";
import {
  FRIENDLY_FALLBACK,
  URL_FRIENDLY_FALLBACK,
  progressLabelForStatus,
  validateScheduleFile,
} from "./scheduleIngestionShared";
import { ScheduleIngestionReview } from "./ScheduleIngestionReview";
import "./ScheduleIngestionModal.css";

// ModalCloseButton (see ModalCloseButton.tsx) does not declare `ref` in its own props, so passing
// one fails typecheck even though React 19 forwards it to the underlying <button> at runtime. This
// modal focuses the close button on open — see the effect below — so the ref is load-bearing.
// This local alias types that without touching the shared primitive.

interface ScheduleIngestionModalProps {
  onClose: () => void;
  /** Seed for the tournament title in the review step (e.g. the event editor's title). */
  defaultTitle?: string;
  /** Optional default timezone (IANA) for the review step. */
  defaultTimeZone?: string;
  /**
   * Optional schedule link to prefill the "paste a link" input with (e.g. the
   * event editor's itinerary link).
   */
  defaultSourceUrl?: string;
  /**
   * When set, this is a REBUILD: the confirm replaces this existing tournament's games instead of
   * creating a new one. Changes the modal copy accordingly.
   */
  targetCalendarEventId?: string;
  /**
   * Fired after a tournament is created (via confirm) or when the user is
   * routed to an already-created tournament. The host should refresh the
   * calendar so the new tournament appears.
   */
  onConfirmed?: (calendarEventId: string) => void;
}

type Phase =
  | "picking"
  | "uploading"
  | "extracting"
  | "review"
  | "empty"
  | "success"
  | "failed";

const POLL_INTERVAL_MS = 3000;
// Big PDFs/images can take several minutes to extract server-side, so wait
// generously before giving up (kept above the server model-call timeout).
const POLL_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
// Per-status-request timeout, well under the poll interval, so a single hung
// request rejects and the loop advances to re-check the overall deadline
// instead of stalling forever.
const STATUS_REQUEST_TIMEOUT_MS = 10000;
// After this many back-to-back poll failures we tell the user it looks like a
// connection problem rather than leaving them on a "still working" spinner.
const MAX_CONSECUTIVE_POLL_FAILURES = 5;
const ACCEPT = ".pdf,image/*";

function isBlockedLinkMessage(message: string): boolean {
  // The backend returns this exact copy on a blocked/unreadable link. Matching
  // it lets us offer the upload fallback affordance rather than a bare retry.
  return message.trim() === URL_FRIENDLY_FALLBACK;
}

type SourceMode = "upload" | "link";

export function ScheduleIngestionModal({
  onClose,
  defaultTitle,
  defaultTimeZone,
  defaultSourceUrl,
  targetCalendarEventId,
  onConfirmed,
}: ScheduleIngestionModalProps) {
  const [phase, setPhase] = useState<Phase>("picking");
  const [sourceMode, setSourceMode] = useState<SourceMode>("upload");
  // Seeded once from the host's itinerary link. This relies on the modal being
  // conditionally mounted (the event editor renders it behind a `showModal &&`
  // guard), so a fresh instance mounts each time it opens and picks up the
  // current URL. If a caller ever keeps this mounted across opens, add an
  // effect to re-seed when defaultSourceUrl changes.
  const [urlInput, setUrlInput] = useState<string>(defaultSourceUrl ?? "");
  const [error, setError] = useState<string>("");
  const [progressLabel, setProgressLabel] = useState<string>("");
  const [ingestionId, setIngestionId] = useState<string>("");
  const [successResult, setSuccessResult] =
    useState<ConfirmScheduleIngestionResponse | null>(null);

  // A single controller for the whole create -> upload -> start -> poll run so
  // that closing the modal (unmount) aborts every in-flight request.
  const runControllerRef = useRef<AbortController | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    return () => {
      runControllerRef.current?.abort();
    };
  }, []);

  // Escape-to-close, matching the convention used by the other modals in the app.
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  // Move focus into the dialog on open so keyboard users start inside it.
  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  const handleFiles = async (files: File[]) => {
    setError("");
    if (files.length === 0) return;

    for (const file of files) {
      const validationError = validateScheduleFile(file);
      if (validationError) {
        setError(files.length === 1 ? validationError : `${file.name}: ${validationError}`);
        setPhase("failed");
        return;
      }
    }

    // Abort any previous run before starting a new one.
    runControllerRef.current?.abort();
    const controller = new AbortController();
    runControllerRef.current = controller;
    const { signal } = controller;

    try {
      setPhase("uploading");
      const newIngestionId = await uploadScheduleFiles(files, signal);
      if (signal.aborted) return;
      setIngestionId(newIngestionId);

      setProgressLabel(progressLabelForStatus("Extracting"));
      setPhase("extracting");
      await startScheduleIngestion(newIngestionId, signal);
      if (signal.aborted) return;

      await pollUntilDone(newIngestionId, signal);
    } catch (err) {
      if (signal.aborted) return;
      setError(getSafeErrorMessage(err, FRIENDLY_FALLBACK));
      setPhase("failed");
    }
  };

  const handleUrlSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const trimmed = urlInput.trim();
    if (!trimmed) {
      // Keep the user on the picker with an inline message rather than jumping
      // to the full-screen failed step for a trivial empty-field mistake.
      setError("Paste a schedule link first.");
      return;
    }

    // Abort any previous run before starting a new one.
    runControllerRef.current?.abort();
    const controller = new AbortController();
    runControllerRef.current = controller;
    const { signal } = controller;

    try {
      // A URL job is enqueued immediately: create, then poll. No upload, no
      // /start call.
      setProgressLabel(progressLabelForStatus("Fetching"));
      setPhase("extracting");
      const newIngestionId = await startScheduleIngestionFromUrl(trimmed, signal);
      if (signal.aborted) return;
      setIngestionId(newIngestionId);

      await pollUntilDone(newIngestionId, signal);
    } catch (err) {
      if (signal.aborted) return;
      // A malformed/unsafe URL fails the create call (HTTP 400). Show the
      // server's message; if it is the blocked-link message the failed step
      // offers the upload fallback.
      setError(getSafeErrorMessage(err, URL_FRIENDLY_FALLBACK));
      setPhase("failed");
    }
  };

  const pollUntilDone = (ingestionId: string, signal: AbortSignal) =>
    new Promise<void>((resolve) => {
      const deadline = Date.now() + POLL_TIMEOUT_MS;
      let consecutiveFailures = 0;

      const poll = async () => {
        if (signal.aborted) {
          resolve();
          return;
        }

        if (Date.now() > deadline) {
          setError(
            "This is taking longer than expected. Please close this and try again."
          );
          setPhase("failed");
          resolve();
          return;
        }

        try {
          const status = await getScheduleIngestionStatus(
            ingestionId,
            signal,
            STATUS_REQUEST_TIMEOUT_MS
          );
          if (signal.aborted) {
            resolve();
            return;
          }

          consecutiveFailures = 0;

          if (status.status === "Completed") {
            const found = status.games ?? [];
            // Zero games is a dead-end (nothing to review); otherwise move the
            // user into the editable review step, which loads the full review
            // payload (games + team matches) itself.
            setPhase(found.length === 0 ? "empty" : "review");
            resolve();
            return;
          }

          if (status.status === "Failed") {
            setError(status.error || FRIENDLY_FALLBACK);
            setPhase("failed");
            resolve();
            return;
          }

          // Still working: keep the extracting state and poll again. The label
          // follows the server status so a URL fetch reads "Reading the link..."
          // before it flips to extraction.
          setProgressLabel(progressLabelForStatus(status.status));
          setPhase("extracting");
        } catch {
          // A single failed poll should not abort the run. Network blips are
          // common; the next tick retries. But if every poll keeps failing,
          // surface a connection-specific message rather than a vague spinner.
          if (signal.aborted) {
            resolve();
            return;
          }
          consecutiveFailures += 1;
          if (consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
            setError(
              "We are having trouble reaching the server. Check your connection and try again."
            );
            setPhase("failed");
            resolve();
            return;
          }
        }

        setTimeout(poll, POLL_INTERVAL_MS);
      };

      void poll();
    });

  const onPickChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    // Reset the input so picking the same file(s) again re-triggers change.
    e.target.value = "";
    if (files.length > 0) void handleFiles(files);
  };

  const handleRetry = () => {
    setError("");
    setProgressLabel("");
    setIngestionId("");
    setSuccessResult(null);
    setPhase("picking");
  };

  // Blocked/unreadable link fallback: send the user back to the picker on the
  // upload tab so they can try a PDF or screenshot without closing the modal.
  const handleSwitchToUpload = () => {
    setError("");
    setProgressLabel("");
    setIngestionId("");
    setSuccessResult(null);
    setSourceMode("upload");
    setPhase("picking");
  };

  const handleConfirmed = (result: ConfirmScheduleIngestionResult) => {
    if (result.kind === "created") {
      // New tournament: show the simple created summary here.
      setSuccessResult(result.response);
      setPhase("success");
      onConfirmed?.(result.response.calendarEventId);
      return;
    }
    // Rebuild (merge): the review panel renders its own "what changed" summary in
    // place, so we stay on the review phase and just refresh the calendar behind it.
    onConfirmed?.(result.result.calendarEventId);
  };

  const handleAlreadyConfirmed = (calendarEventId: string) => {
    // The tournament already exists: refresh the calendar and close so the user
    // lands back on the calendar where the tournament is visible.
    onConfirmed?.(calendarEventId);
    onClose();
  };

  return (
    <div
      className="ingest-overlay"
      role="presentation"
      onClick={onClose}
      data-testid="schedule-ingestion-overlay"
    >
      <div
        className={`ingest-modal${
          phase === "review" ? " ingest-modal--wide" : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ingest-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ingest-head">
          <h2 id="ingest-title" className="ingest-title">
            {targetCalendarEventId ? "Rebuild schedule" : "Auto-fill schedule"}
          </h2>
          <ModalCloseButton
            ref={closeButtonRef}
            onClick={onClose}
            data-testid="schedule-ingestion-close"
          />
        </div>

        <div className="ingest-body">
          {phase === "picking" && (
            <div className="ingest-picker">
              {/* A plain two-button toggle group. Not an ARIA tablist: that
                  pattern also requires tabpanels and arrow-key roving, which we
                  do not implement, so aria-pressed on toggle buttons is the more
                  honest and complete semantics here. */}
              <div
                className="ingest-modes"
                role="group"
                aria-label="Schedule source"
              >
                <button
                  type="button"
                  aria-pressed={sourceMode === "upload"}
                  className={`ingest-mode${
                    sourceMode === "upload" ? " ingest-mode--active" : ""
                  }`}
                  onClick={() => {
                    setError("");
                    setSourceMode("upload");
                  }}
                  data-testid="schedule-mode-upload"
                >
                  Upload a PDF or screenshot
                </button>
                <button
                  type="button"
                  aria-pressed={sourceMode === "link"}
                  className={`ingest-mode${
                    sourceMode === "link" ? " ingest-mode--active" : ""
                  }`}
                  onClick={() => {
                    setError("");
                    setSourceMode("link");
                  }}
                  data-testid="schedule-mode-link"
                >
                  Paste a schedule link
                </button>
              </div>

              {sourceMode === "upload" ? (
                <>
                  <p className="ingest-lead">
                    Upload one or more schedule PDFs or screenshots and we will
                    pull out the games for you.
                  </p>
                  <label className="ingest-drop" htmlFor="schedule-file-input">
                    <span className="ingest-drop__icon">⬆</span>
                    <span className="ingest-drop__label">
                      Choose PDFs or images
                    </span>
                    <span className="ingest-drop__hint">
                      PDF, PNG, or JPG — pick several to combine
                    </span>
                    <input
                      id="schedule-file-input"
                      type="file"
                      accept={ACCEPT}
                      multiple
                      className="ingest-file-input"
                      onChange={onPickChange}
                      data-testid="schedule-file-input"
                    />
                  </label>
                </>
              ) : (
                <form className="ingest-url-form" onSubmit={handleUrlSubmit}>
                  <p className="ingest-lead">
                    Paste a link to the schedule and we will pull out the games
                    for you.
                  </p>
                  <label className="ingest-url-label" htmlFor="schedule-url-input">
                    Schedule link
                  </label>
                  <input
                    id="schedule-url-input"
                    type="url"
                    className="ingest-url-input"
                    placeholder="https://…/schedule"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    data-testid="schedule-url-input"
                  />
                  {error && (
                    <p className="ingest-url-error" role="alert">
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    className="ingest-btn ingest-btn--primary"
                    data-testid="schedule-url-submit"
                  >
                    Read this link
                  </button>
                  <p className="ingest-subtext">
                    Some sites block automated reading. If that happens, upload
                    a PDF or screenshot instead.
                  </p>
                </form>
              )}
            </div>
          )}

          {(phase === "uploading" || phase === "extracting") && (
            <div className="ingest-progress" data-testid="schedule-ingestion-progress">
              <div className="ingest-spinner" />
              <p className="ingest-phase">
                {phase === "uploading"
                  ? "Uploading your schedule..."
                  : progressLabel ||
                    "Reading the games from your schedule..."}
              </p>
              <p className="ingest-subtext">This can take a minute.</p>
            </div>
          )}

          {phase === "failed" && (
            <div className="ingest-error" data-testid="schedule-ingestion-error">
              <div className="ingest-error__icon" aria-hidden="true">
                ⚠️
              </div>
              <p className="ingest-error__msg" role="alert">
                {error}
              </p>
              {isBlockedLinkMessage(error) ? (
                <div className="ingest-error__actions">
                  <button
                    type="button"
                    className="ingest-btn ingest-btn--primary"
                    onClick={handleSwitchToUpload}
                    data-testid="schedule-ingestion-upload-instead"
                  >
                    Upload a PDF or screenshot
                  </button>
                  <button
                    type="button"
                    className="ingest-btn"
                    onClick={handleRetry}
                    data-testid="schedule-ingestion-retry"
                  >
                    Try a different link
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="ingest-btn ingest-btn--primary"
                  onClick={handleRetry}
                  data-testid="schedule-ingestion-retry"
                >
                  Try again
                </button>
              )}
            </div>
          )}

          {phase === "empty" && (
            <div className="ingest-empty" data-testid="schedule-ingestion-empty">
              {sourceMode === "link" ? (
                <p>
                  We read that page but didn't find a game schedule on it. Many sites load their
                  schedule after clicking or scrolling, which we can't see. Try a direct link to the
                  schedule page, or upload a PDF or screenshot instead.
                </p>
              ) : (
                <p>We didn't find any games in that file. Try a clearer PDF or screenshot.</p>
              )}
              <button
                type="button"
                className="ingest-btn ingest-btn--primary"
                onClick={handleRetry}
              >
                {sourceMode === "link" ? "Try a different source" : "Try another file"}
              </button>
            </div>
          )}

          {phase === "review" && ingestionId && (
            <ScheduleIngestionReview
              ingestionId={ingestionId}
              targetCalendarEventId={targetCalendarEventId}
              defaultTitle={defaultTitle}
              defaultTimeZone={defaultTimeZone}
              onConfirmed={handleConfirmed}
              onAlreadyConfirmed={handleAlreadyConfirmed}
            />
          )}

          {phase === "success" && successResult && (
            <div className="review-success" data-testid="schedule-ingestion-success">
              <div className="review-success__icon" aria-hidden="true">
                ✅
              </div>
              <p className="review-success__msg">
                {successResult.title} is on the calendar.
              </p>
              <p className="review-success__detail">
                {successResult.gamesCreated}{" "}
                {successResult.gamesCreated === 1 ? "game" : "games"} added
                {successResult.teamsCreated > 0
                  ? `, ${successResult.teamsCreated} new ${
                      successResult.teamsCreated === 1 ? "team" : "teams"
                    } created`
                  : ""}
                .
              </p>
              <button
                type="button"
                className="ingest-btn ingest-btn--primary"
                onClick={onClose}
                data-testid="schedule-ingestion-success-done"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
